import { Check, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";

import type { MapLeadAnalysis, MapLeadItem } from "../../api/mapLeadService";
import { AnalysisCard } from "./AnalysisCard";
import { leadLevelLabel } from "../../model/leadLevel";
import { usePersona } from "@/shared/brand/persona";

interface Props {
  item: MapLeadItem | null;
  /** 军师分析单次积分价(服务端价目表;null = 价表未到/取失败,不编数,退「按次计费」字样)。 */
  analyzePrice: number | null;
  joined: boolean;
  onConvert: (item: MapLeadItem) => void;
  analysis: MapLeadAnalysis | null;
  analyzing: boolean;
  onAnalyze: (item: MapLeadItem) => void;
}

/**
 * 企业详情(右栏)。只展示高德 business 真实字段 —— 工商字段(成立时间/注册资本/人员规模)
 * 高德 POI 不提供,定稿拍板⑦砍掉,不要往回加。「让军师分析」P2 接入(按次计费),P1 占位禁用。
 */
export function LeadDetailDrawer({
  item,
  analyzePrice,
  joined,
  onConvert,
  analysis,
  analyzing,
  onAnalyze,
}: Props) {
  const persona = usePersona();
  if (!item) {
    return (
      <div className="grid h-full min-h-[320px] place-items-center rounded-lg border bg-card p-6 text-center text-xs text-muted-foreground">
        点击列表或地图上的企业,查看详情
      </div>
    );
  }

  const kv: [string, string | null][] = [
    ["电话", item.phone],
    ["地址", item.address],
    [
      "距离",
      item.distanceMeters == null
        ? null
        : item.distanceMeters < 1000
          ? `${item.distanceMeters}m`
          : `${(item.distanceMeters / 1000).toFixed(1)}km`,
    ],
    ["营业时间", item.openTime],
    ["评分", item.rating == null ? null : String(item.rating)],
    ["商圈", item.businessArea],
    ["POI ID", item.poiId],
    ["来源", "高德地图 POI"],
  ];

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto rounded-lg border bg-card p-4">
      <div>
        <h3 className="text-sm font-bold leading-snug">{item.name}</h3>
        <div className="mt-1 flex flex-wrap gap-1">
          {item.industry && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-2xs text-primary">
              {item.industry}
            </span>
          )}
          {item.phone && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-2xs text-primary">
              有电话
            </span>
          )}
        </div>
      </div>

      <div>
        <p className="text-2xs uppercase tracking-wide text-muted-foreground">商机评分(规则)</p>
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-extrabold tabular-nums text-orange-500">{item.score}</span>
          <span className="text-xs text-muted-foreground">/ 100</span>
          <span className="ml-auto rounded-full bg-orange-500/10 px-2 py-0.5 text-2xs text-orange-600">
            {leadLevelLabel(item.level, item.score)}
          </span>
        </div>
      </div>

      <div className="space-y-1.5">
        {item.scoreDetail.map((d) => (
          <div key={d.label} className="grid grid-cols-[64px_1fr_32px] items-center gap-2 text-2xs">
            <span className="text-muted-foreground">{d.label}</span>
            <span className="h-1 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-primary"
                style={{ width: `${d.max === 0 ? 0 : Math.round((d.points / d.max) * 100)}%` }}
              />
            </span>
            <span className="text-right tabular-nums">+{d.points}</span>
          </div>
        ))}
      </div>

      <dl className="grid grid-cols-[56px_1fr] gap-x-2 gap-y-1 border-t pt-3 text-xs">
        {kv
          .filter(([, v]) => v != null && v !== "")
          .map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="break-all">{v}</dd>
            </div>
          ))}
      </dl>

      {analysis && <AnalysisCard analysis={analysis} />}

      <div className="mt-auto space-y-2 pt-2">
        <Button className="w-full" disabled={joined} onClick={() => onConvert(item)}>
          {joined ? (
            <>
              <Check className="mr-1.5 h-4 w-4" />
              已在线索池
            </>
          ) : (
            "加入线索池"
          )}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          disabled={analyzing}
          onClick={() => onAnalyze(item)}
          title="未入池会先自动加入线索;分析失败不扣积分"
        >
          {analyzing ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              {persona.title}分析中…
            </>
          ) : (
            `${analysis ? `重新让${persona.title}分析` : `让${persona.title}分析`} · ${
              analyzePrice != null ? `${analyzePrice} 积分/次` : "按次计费"
            }`
          )}
        </Button>
      </div>
    </div>
  );
}
